/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { jwtDecode } from 'jwt-decode';
import { useNavigate, Link } from 'react-router-dom';
import Pagination from 'react-js-pagination';
import Navbar from '../Navbar';
import Header from '../Header';
import baseUrl from '../BaseUrl';
import UserSegmentToggle from '../common/UserSegmentToggle';
import { useUserSegment, watchFilterValue } from '../../context/UserSegmentContext';
import MostActiveFilters from './MostActiveFilters';
import MostActiveTable from './MostActiveTable';
import ActiveDaysModal from './ActiveDaysModal';
import ExportButton from './ExportButton';
import CohortPanel from './CohortPanel';
import EmptyState from './states/EmptyState';
import ErrorState from './states/ErrorState';
import TableSkeleton from './states/TableSkeleton';
import './active-users.css';

// "Number of Active Days" dropdown → backend `active_days_filter` param value.
const ACTIVE_DAYS_RANGE_TOKENS = ['5-10', '11-15', '16-20', '21-30'];
const mapActiveDaysToParam = (value) => {
  if (value === '' || value == null || value === 'All') return null;
  if (value === '31+') return '31+';
  return ACTIVE_DAYS_RANGE_TOKENS.includes(value) ? value : null;
};

const formatDate = (date) => {
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
};

// The cohort graph is driven by the Registration bucket ALONE — it derives its own window from
// the bucket and deliberately ignores the manually-editable "From/To active days" pickers (those
// only scope the ranking table). "Active Date" (or any non-bucket value) → send no range so the
// backend applies its granularity-aware default (last 12 months / last 12 weeks).
const cohortWindowForFilter = (regFilter) => {
  const today = new Date();
  let startDays;
  let endDays;
  switch (regFilter) {
    case '0-30': startDays = 0; endDays = 30; break;
    case '31-90': startDays = 31; endDays = 90; break;
    case '91-120': startDays = 91; endDays = 120; break;
    case '120+': startDays = 121; endDays = null; break;
    default: return {}; // 'active' → backend default window
  }
  if (endDays !== null) {
    const to = new Date(today); to.setDate(today.getDate() - startDays);
    const from = new Date(today); from.setDate(today.getDate() - endDays);
    return { from_date: formatDate(from), to_date: formatDate(to) };
  }
  const cutoff = new Date(today); cutoff.setDate(today.getDate() - startDays);
  return { from_date: formatDate(cutoff) }; // 120+ → open-ended
};

// Upper bound for "export all filtered rows" (one un-paginated pass).
const EXPORT_LIMIT = 100000;

const MAU_EXPORT_COLUMNS = [
  { key: 'name', label: 'Name' },
  { key: 'email', label: 'Email' },
  { key: 'watch_name', label: 'Watch Name' },
  { key: 'data_source', label: 'Data Sources' },
  { key: 'country', label: 'Country' },
  { key: 'platform', label: 'Platform' },
  { key: 'subscription_status', label: 'Plan Info' },
  { key: 'registered_date', label: 'Registered Date' },
  { key: 'last_seen', label: 'Last Active (UTC)' },
  { key: 'active_days_count', label: 'Active Days' },
  { key: 'total_newopen', label: 'New Open' },
  { key: 'total_resume', label: 'Resume' },
  { key: 'total_activity', label: 'Total Activity count' },
  { key: 'meditation_count', label: 'Meditation' },
  { key: 'breath_count', label: 'Breath' },
  { key: 'monitor_count', label: 'Monitor' },
  { key: 'alerts_count', label: 'Alerts' },
  { key: 'bp_count', label: 'BP' },
  { key: 'sugar_count', label: 'Sugar' },
  { key: 'oxygen_count', label: 'Oxygen' },
  { key: 'reports_count', label: 'Reports' },
  { key: 'event_logs_count', label: 'C&C' },
];

const MostActiveUsers = () => {
  const navigate = useNavigate();
  const [token, setToken] = useState('');
  const [expire, setExpire] = useState('');
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [totalItem, setTotalItem] = useState(0);

  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [displayFromDate, setDisplayFromDate] = useState(null);
  const [displayToDate, setDisplayToDate] = useState(null);

  const [showDatesDialog, setShowDatesDialog] = useState(false);
  const [selectedUserDates, setSelectedUserDates] = useState([]);
  const [selectedUserName, setSelectedUserName] = useState('');

  const [searchQuery, setSearchQuery] = useState('');
  const [sortOrder, setSortOrder] = useState('DESC');
  const [sortField, setSortField] = useState('active_days_count');
  const [registrationFilter, setRegistrationFilter] = useState('active');
  const [platformFilter, setPlatformFilter] = useState('');
  const [activeDaysFilter, setActiveDaysFilter] = useState('');
  const [cohortData, setCohortData] = useState(null);
  const [cohortLoading, setCohortLoading] = useState(true);
  const [cohortError, setCohortError] = useState('');
  const [cohortGranularity, setCohortGranularity] = useState('week');   // Weekly by default
  const { segment } = useUserSegment();   // global watch/all segment (feature 023)

  useEffect(() => {
    const today = new Date();
    const priorDate = new Date();
    priorDate.setDate(today.getDate() - 29);
    setFromDate(formatDate(priorDate));
    setToDate(formatDate(today));
    setDisplayFromDate(priorDate);
    setDisplayToDate(today);
    refreshToken();
  }, []);

  useEffect(() => {
    if (token) {
      fetchUsers(1, fromDate, toDate);
      fetchCohorts(cohortGranularity, registrationFilter);
    }
  }, [token]);

  // Re-fetch the ranking when the global watch segment changes
  // (skip initial mount; wait until we have a token).
  const didMountSegment = useRef(false);
  useEffect(() => {
    if (!didMountSegment.current) { didMountSegment.current = true; return; }
    if (!token) return;
    setPage(1);
    fetchUsers(1, fromDate, toDate);
  }, [segment]);

  const refreshToken = async () => {
    try {
      const res = await axios.get(`${baseUrl}/admin/api/token`);
      setToken(res.data.accessToken);
      setExpire(jwtDecode(res.data.accessToken).exp);
    } catch (err) {
      navigate('/');
      window.location.reload();
    }
  };

  const axiosJWT = axios.create();
  axiosJWT.interceptors.request.use(async (config) => {
    const now = new Date();
    if (expire * 1000 < now.getTime()) {
      const res = await axios.get(`${baseUrl}/admin/api/token`);
      config.headers.Authorization = `Bearer ${res.data.accessToken}`;
      setToken(res.data.accessToken);
      setExpire(jwtDecode(res.data.accessToken).exp);
    }
    return config;
  }, (err) => Promise.reject(err));

  const fetchUsers = async (
    pageNum, from, to,
    search = searchQuery, sort = sortOrder, sField = sortField,
    regFilter = registrationFilter, platform = platformFilter, activeDays = activeDaysFilter
  ) => {
    setLoading(true);
    setError('');
    try {
      const params = { page: pageNum, limit: 20, from_date: from, to_date: to, search, sort, sort_field: sField };
      if (regFilter) params.registration_filter = regFilter;
      if (platform) params.platform = platform;
      const activeDaysParam = mapActiveDaysToParam(activeDays);
      if (activeDaysParam !== null) params.active_days_filter = activeDaysParam;
      { const wf = watchFilterValue(segment); if (wf) params.watch_filter = wf; }   // feature 023 (watch / without_watch)

      const res = await axiosJWT.get(`${baseUrl}/admin/api/most-frequent-users`, {
        params, headers: { Authorization: `Bearer ${token}` },
      });
      setUsers(res.data.data || []);
      setTotalItem(res.data.totalItem || 0);
    } catch (err) {
      if (err.response && err.response.status === 401) { navigate('/'); return; }
      setError('We couldn’t load the active-user ranking. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Fetch the retention cohorts (US1/US2). The window follows the Registration filter:
  // "active" → last 12 signup months; a bounded bucket → that registration window.
  const fetchCohorts = async (granularity, regFilter) => {
    setCohortLoading(true);
    setCohortError('');
    try {
      const params = { granularity, ...cohortWindowForFilter(regFilter) };
      const res = await axiosJWT.get(`${baseUrl}/admin/api/most-active-cohorts`, {
        params, headers: { Authorization: `Bearer ${token}` },
      });
      setCohortData(res.data && res.data.success ? res.data.data : null);
    } catch (err) {
      setCohortData(null);
      setCohortError('We couldn’t load the retention cohorts. Please try again.');
    } finally {
      setCohortLoading(false);
    }
  };

  const handleGranularityChange = (granularity) => {
    if (granularity === cohortGranularity) return;
    setCohortGranularity(granularity);
    fetchCohorts(granularity, registrationFilter);
  };

  // Drill into the users behind a clicked cohort cell (period number) or Size cell ('all').
  // Carry the grid's exact registration window so the details page clips edge buckets the same
  // way the grid did (otherwise a partial first/last week/month would over-count).
  const handleCohortCellClick = (cohort, period) => {
    const params = new URLSearchParams({ granularity: cohortGranularity, cohort });
    if (period !== 'all') params.set('period', String(period));
    if (cohortData && cohortData.windowStart) params.set('window_start', cohortData.windowStart);
    if (cohortData && cohortData.windowEndExclusive) params.set('window_end_excl', cohortData.windowEndExclusive);
    navigate(`/cohort-details?${params.toString()}`);
  };

  // Fetch every filtered row (un-paginated) for CSV export, matching the
  // current filters/sort. Flattens New Open + Resume into a Total column.
  const fetchAllForExport = async () => {
    const params = {
      page: 1, limit: EXPORT_LIMIT, from_date: fromDate, to_date: toDate,
      search: searchQuery, sort: sortOrder, sort_field: sortField,
    };
    if (registrationFilter) params.registration_filter = registrationFilter;
    if (platformFilter) params.platform = platformFilter;
    const activeDaysParam = mapActiveDaysToParam(activeDaysFilter);
    if (activeDaysParam !== null) params.active_days_filter = activeDaysParam;
    { const wf = watchFilterValue(segment); if (wf) params.watch_filter = wf; }   // feature 023 (watch / without_watch)

    const res = await axiosJWT.get(`${baseUrl}/admin/api/most-frequent-users`, {
      params, headers: { Authorization: `Bearer ${token}` },
    });
    return (res.data.data || []).map((u) => ({
      ...u,
      total_activity: (Number(u.total_newopen) || 0) + (Number(u.total_resume) || 0),
    }));
  };

  const handleFromChange = (date) => { setDisplayFromDate(date); setFromDate(formatDate(date)); };
  const handleToChange = (date) => { setDisplayToDate(date); setToDate(formatDate(date)); };

  const handleRegistrationFilterChange = (filterValue) => {
    setRegistrationFilter(filterValue);
    const today = new Date();

    if (filterValue === 'active') {
      const priorDate = new Date();
      priorDate.setDate(today.getDate() - 29);
      setFromDate(formatDate(priorDate));
      setToDate(formatDate(today));
      setDisplayFromDate(priorDate);
      setDisplayToDate(today);
      fetchCohorts(cohortGranularity, 'active');
      return;
    }

    let startDays;
    let endDays;
    switch (filterValue) {
      case '0-30': startDays = 0; endDays = 30; break;
      case '31-90': startDays = 31; endDays = 90; break;
      case '91-120': startDays = 91; endDays = 120; break;
      case '120+': startDays = 121; endDays = null; break;
      default: return;
    }

    if (endDays !== null) {
      const newToDate = new Date(today); newToDate.setDate(today.getDate() - startDays);
      const newFromDate = new Date(today); newFromDate.setDate(today.getDate() - endDays);
      setFromDate(formatDate(newFromDate));
      setToDate(formatDate(newToDate));
      setDisplayFromDate(newFromDate);
      setDisplayToDate(newToDate);
      fetchCohorts(cohortGranularity, filterValue);
    } else {
      const cutoffDate = new Date(today); cutoffDate.setDate(today.getDate() - startDays);
      setFromDate(formatDate(cutoffDate));
      setToDate('');
      setDisplayFromDate(cutoffDate);
      setDisplayToDate(null);
      fetchCohorts(cohortGranularity, filterValue);
    }
  };

  const handleSearch = () => {
    setPage(1);
    fetchUsers(1, fromDate, toDate, searchQuery, sortOrder, sortField, registrationFilter, platformFilter, activeDaysFilter);
    fetchCohorts(cohortGranularity, registrationFilter);
  };

  const clearFilter = () => {
    const today = new Date();
    const priorDate = new Date();
    priorDate.setDate(today.getDate() - 29);
    const defFrom = formatDate(priorDate);
    const defTo = formatDate(today);

    setFromDate(defFrom); setToDate(defTo);
    setDisplayFromDate(priorDate); setDisplayToDate(today);
    setSearchQuery(''); setSortOrder('desc'); setSortField('active_days_count');
    setRegistrationFilter('active'); setPlatformFilter(''); setActiveDaysFilter(''); setPage(1);

    fetchUsers(1, defFrom, defTo, '', 'desc', 'active_days_count', 'active', '', '');
    fetchCohorts(cohortGranularity, 'active');
  };

  const handlePageChange = (pageNum) => {
    setPage(pageNum);
    fetchUsers(pageNum, fromDate, toDate, searchQuery, sortOrder, sortField, registrationFilter, platformFilter, activeDaysFilter);
  };

  const handleSort = (field) => {
    const order = sortField === field && sortOrder === 'ASC' ? 'DESC' : 'ASC';
    setSortField(field); setSortOrder(order); setPage(1);
    fetchUsers(1, fromDate, toDate, searchQuery, order, field, registrationFilter, platformFilter, activeDaysFilter);
  };

  const handleActiveDaysClick = (user) => {
    setSelectedUserDates(user.active_days_list || []);
    setSelectedUserName(user.name || '-');
    setShowDatesDialog(true);
  };

  const closeDialog = () => { setShowDatesDialog(false); setSelectedUserDates([]); setSelectedUserName(''); };

  const defaultFrom = formatDate(new Date(new Date().setDate(new Date().getDate() - 29)));
  const showClear =
    searchQuery.trim() !== '' ||
    registrationFilter !== 'active' ||
    platformFilter !== '' ||
    activeDaysFilter !== '' ||
    fromDate !== defaultFrom ||
    toDate !== formatDate(new Date());

  return (
    <div className="panelbox">
      <Navbar />
      <div className="right-panel">
        <Header name="Pauser" />
        <div className="content-page">
          <div className="au-head">
            <span className="au-breadcrumb">People › <b>Most active users</b></span>
            <span className="au-route">/unique-users-count</span>
            <span className="au-spacer" />
            <UserSegmentToggle />
            <span className="au-back"><Link to="/dashboard">← Back</Link></span>
          </div>
          <h1 className="au-title">Most active users</h1>

          <MostActiveFilters
            registrationFilter={registrationFilter}
            platformFilter={platformFilter}
            activeDaysFilter={activeDaysFilter}
            searchQuery={searchQuery}
            displayFrom={displayFromDate}
            displayTo={displayToDate}
            showClear={showClear}
            onRegistrationChange={handleRegistrationFilterChange}
            onPlatformChange={setPlatformFilter}
            onActiveDaysChange={setActiveDaysFilter}
            onSearchChange={setSearchQuery}
            onFromChange={handleFromChange}
            onToChange={handleToChange}
            onSearch={handleSearch}
            onClear={clearFilter}
          />

          <CohortPanel
            data={cohortData}
            loading={cohortLoading}
            error={cohortError}
            granularity={cohortGranularity}
            onGranularityChange={handleGranularityChange}
            onCellClick={handleCohortCellClick}
            onRetry={() => fetchCohorts(cohortGranularity, registrationFilter)}
          />

          {loading ? (
            <div className="au-panel"><TableSkeleton rows={8} /></div>
          ) : error ? (
            <ErrorState message={error} onRetry={() => fetchUsers(page, fromDate, toDate)} />
          ) : users.length === 0 ? (
            <EmptyState title="No active users" hasFilters={showClear} onClear={clearFilter}
              message={showClear ? 'No users match the current filters.' : 'There are no active users to show yet.'} />
          ) : (
            <div className="au-panel">
              <div className="au-panel__head">
                <h4 className="au-panel__title">Ranking</h4>
                <span className="au-spacer" />
                <ExportButton columns={MAU_EXPORT_COLUMNS} fetchRows={fetchAllForExport} filename="most-active-users.csv" />
              </div>
              <MostActiveTable
                users={users}
                page={page}
                sortField={sortField}
                sortOrder={sortOrder}
                onSort={handleSort}
                onActiveDaysClick={handleActiveDaysClick}
              />
              {totalItem > 20 && (
                <Pagination
                  activePage={page}
                  itemsCountPerPage={20}
                  totalItemsCount={totalItem}
                  pageRangeDisplayed={5}
                  onChange={handlePageChange}
                />
              )}
            </div>
          )}

          <ActiveDaysModal
            open={showDatesDialog}
            userName={selectedUserName}
            dates={selectedUserDates}
            onClose={closeDialog}
          />
        </div>
      </div>
    </div>
  );
};

export default MostActiveUsers;
